import type { IndustrialCaseSummary } from '@/domain/industrialCase/industrialCaseSummary';

import { IndustrialCaseSummaryCard } from './IndustrialCaseSummaryCard';

type Props = {
  cases: ReadonlyArray<IndustrialCaseSummary>;
  title: string;
  readLabel: string;
  metricLabel: string;
  /** Reports which case was opened from here */
  onSelect?: (slug: string) => void;
};

/**
 * ### IndustrialCaseRelatedCases
 *
 * Offers the nearest published cases at the end of a case.
 *
 * Which cases these are is decided on the server by
 * `selectRelatedIndustrialCases`; this only lays them out. Nothing renders when
 * there is no sibling case, which is the normal state while few cases are
 * published — an empty "related cases" heading would suggest a failure.
 *
 * @param props The cases to offer, their labels, and an optional click reporter
 * @returns The related-case section, or nothing when there is none to show
 */
export function IndustrialCaseRelatedCases({
  cases,
  title,
  readLabel,
  metricLabel,
  onSelect,
}: Props) {
  if (cases.length === 0) return null;

  return (
    <section className="relative z-1 grid border-t border-[#d8d8d4] md:grid-cols-4">
      <div className="px-5 py-14 md:col-span-3 md:col-start-2 md:px-10 md:py-16 lg:px-20">
        <h2 className="text-[clamp(24px,2.8vw,32px)] font-semibold leading-[1.2] tracking-[-0.025em] text-apps-brandGrayBlack">
          {title}
        </h2>
        <ul className="mt-8 grid list-none gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cases.map((caseSummary) => (
            <li className="min-w-0" key={caseSummary.slug}>
              <IndustrialCaseSummaryCard
                caseSummary={caseSummary}
                readLabel={readLabel}
                metricLabel={metricLabel}
                onSelect={
                  onSelect ? () => onSelect(caseSummary.slug) : undefined
                }
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
